import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bookmark,
  BookmarkPlus,
  Check,
  MoreVertical,
  Pencil,
  Pin,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import type { SavedFilterDto } from "@/types/api";
import { savedFiltersApi } from "@/api/ux";
import { useDialogs } from "@/hooks/useDialogs";
import { Button, Input } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";

export interface SavedFilterBarProps<TFilter> {
  pageKey: string;
  /** Current filter/search/sort state — serialized to filterJson. */
  currentState: TFilter;
  /** Apply a saved filter to the host page's state. */
  onApply: (state: TFilter) => void;
  className?: string;
}

/**
 * UX Enhancements §9. Tabs-style saved-view strip above any DataTable.
 * Supports create / rename / delete / set-default / update-with-current-state.
 * Default view auto-applies on mount.
 */
export function SavedFilterBar<TFilter>({
  pageKey,
  currentState,
  onApply,
  className,
}: SavedFilterBarProps<TFilter>) {
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const appliedDefaultOnce = useRef(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const listQuery = useQuery({
    queryKey: ["saved-filters", pageKey],
    queryFn: () => savedFiltersApi.list(pageKey),
  });

  const views = listQuery.data ?? [];

  // Auto-apply the default view on first load.
  useEffect(() => {
    if (appliedDefaultOnce.current || !views.length) return;
    const def = views.find((v) => v.isDefault);
    if (def) {
      try {
        const parsed = JSON.parse(def.filterJson) as TFilter;
        setActiveId(def.id);
        onApply(parsed);
      } catch {
        /* ignore malformed JSON */
      }
    }
    appliedDefaultOnce.current = true;
  }, [views, onApply]);

  useEffect(() => {
    if (!openMenuId) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [openMenuId]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["saved-filters", pageKey] });

  const createMutation = useMutation({
    mutationFn: () =>
      savedFiltersApi.create({
        pageKey,
        name: newName.trim(),
        filterJson: JSON.stringify(currentState),
        isDefault: false,
      }),
    onSuccess: (row) => {
      invalidate();
      setActiveId(row.id);
      setCreating(false);
      setNewName("");
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => savedFiltersApi.rename(id, name),
    onSuccess: () => {
      invalidate();
      setRenamingId(null);
      setRenameValue("");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (id: string) =>
      savedFiltersApi.updateJson(id, JSON.stringify(currentState)),
    onSuccess: invalidate,
  });

  const defaultMutation = useMutation({
    mutationFn: (id: string) => savedFiltersApi.setDefault(id),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => savedFiltersApi.remove(id),
    onSuccess: (_data, id) => {
      if (activeId === id) setActiveId(null);
      invalidate();
    },
  });

  const applyView = (view: SavedFilterDto) => {
    try {
      const parsed = JSON.parse(view.filterJson) as TFilter;
      setActiveId(view.id);
      onApply(parsed);
    } catch {
      /* ignore malformed JSON */
    }
  };

  const canSaveCurrent = useMemo(() => {
    try {
      return JSON.stringify(currentState).length > 0;
    } catch {
      return false;
    }
  }, [currentState]);

  return (
    <div className={cn("flex flex-wrap items-center gap-space-2", className)}>
      <div className="flex items-center gap-space-1 text-label font-display font-semibold uppercase tracking-label text-gold-shadow">
        <Bookmark size={14} strokeWidth={1.5} aria-hidden />
        Views
      </div>

      {listQuery.isLoading && (
        <span className="text-label text-gray-mid">Loading…</span>
      )}

      {views.map((view) => (
        <div key={view.id} className="relative flex items-center">
          {renamingId === view.id ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (renameValue.trim()) {
                  renameMutation.mutate({ id: view.id, name: renameValue.trim() });
                }
              }}
              className="flex items-center gap-space-1"
            >
              <Input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="h-8 w-40 text-label"
              />
              <Button variant="primary" size="sm" type="submit">
                <Check size={12} strokeWidth={1.5} aria-hidden />
              </Button>
              <Button
                variant="text"
                size="sm"
                type="button"
                onClick={() => {
                  setRenamingId(null);
                  setRenameValue("");
                }}
              >
                <X size={12} strokeWidth={1.5} aria-hidden />
              </Button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => applyView(view)}
              className={cn(
                "inline-flex items-center gap-space-1 px-space-3 h-8 rounded-md border",
                "font-display text-label font-semibold uppercase tracking-label",
                activeId === view.id
                  ? "bg-onyx text-gold border-onyx"
                  : "bg-white-pure text-onyx border-border-light hover:bg-gold-highlight/10"
              )}
            >
              {view.isDefault && (
                <Pin size={10} strokeWidth={1.5} className="text-gold" aria-hidden />
              )}
              <span className="normal-case tracking-normal font-semibold">{view.name}</span>
            </button>
          )}

          {renamingId !== view.id && (
            <button
              type="button"
              onClick={() => setOpenMenuId(openMenuId === view.id ? null : view.id)}
              aria-label="View options"
              className="inline-flex h-8 w-6 items-center justify-center text-gray-mid hover:text-onyx"
            >
              <MoreVertical size={14} strokeWidth={1.5} aria-hidden />
            </button>
          )}

          {openMenuId === view.id && (
            <div
              ref={menuRef}
              role="menu"
              className="absolute top-10 left-0 z-30 w-52 rounded-md border border-border-light bg-white-pure shadow-lg"
            >
              <MenuRow
                icon={<RefreshCw size={14} strokeWidth={1.5} aria-hidden />}
                label="Update with current filters"
                onClick={() => {
                  updateMutation.mutate(view.id);
                  setOpenMenuId(null);
                }}
              />
              <MenuRow
                icon={<Pencil size={14} strokeWidth={1.5} aria-hidden />}
                label="Rename"
                onClick={() => {
                  setRenamingId(view.id);
                  setRenameValue(view.name);
                  setOpenMenuId(null);
                }}
              />
              <MenuRow
                icon={<Pin size={14} strokeWidth={1.5} aria-hidden />}
                label={view.isDefault ? "Default view" : "Set as default"}
                disabled={view.isDefault}
                onClick={() => {
                  defaultMutation.mutate(view.id);
                  setOpenMenuId(null);
                }}
              />
              <MenuRow
                icon={<Trash2 size={14} strokeWidth={1.5} aria-hidden />}
                label="Delete view"
                destructive
                onClick={async () => {
                  const ok = await dialogs.confirm({
                    title: "Delete saved view?",
                    message: `Delete view "${view.name}"?`,
                    confirmLabel: "Delete",
                    destructive: true,
                  });
                  if (ok) {
                    deleteMutation.mutate(view.id);
                    setOpenMenuId(null);
                  }
                }}
              />
            </div>
          )}
        </div>
      ))}

      {creating ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) createMutation.mutate();
          }}
          className="flex items-center gap-space-1"
        >
          <Input
            autoFocus
            placeholder="View name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="h-8 w-40 text-label"
          />
          <Button
            variant="primary"
            size="sm"
            type="submit"
            disabled={!newName.trim() || createMutation.isPending}
          >
            <Check size={12} strokeWidth={1.5} aria-hidden /> Save
          </Button>
          <Button
            variant="text"
            size="sm"
            type="button"
            onClick={() => {
              setCreating(false);
              setNewName("");
            }}
          >
            <X size={12} strokeWidth={1.5} aria-hidden />
          </Button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={!canSaveCurrent}
          className={cn(
            "inline-flex items-center gap-space-1 h-8 px-space-3 rounded-md",
            "font-display text-label font-semibold uppercase tracking-label",
            "border border-dashed border-border-light text-gold-shadow",
            "hover:border-gold hover:text-gold",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          <BookmarkPlus size={14} strokeWidth={1.5} aria-hidden /> Save current view
        </button>
      )}
    </div>
  );
}

function MenuRow({
  icon,
  label,
  onClick,
  disabled,
  destructive,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small text-left",
        "hover:bg-white-warm disabled:opacity-50 disabled:cursor-not-allowed",
        destructive && "text-status-error hover:bg-status-error/10"
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
